"use client";

// FILE: src/shared/components/cookie-banner.tsx
// VERSION: 1.2.0
// START_MODULE_CONTRACT:
// PURPOSE: Компонент уведомления об использовании файлов cookie — только информирование и явный выбор пользователя, без согласия на обработку персональных данных. Два варианта отображения: CRM (корпоративный, сине-белый) и Public/Portal (тёмный брутальный с янтарным акцентом).
// SCOPE: Определение публичной страницы (isPublicPage), чтение/запись localStorage (oxpa_cookie_consent — приняты все cookie; oxpa_cookie_choice — выбраны только необходимые), два визуальных варианта баннера.
// INPUT: pathname из usePathname().
// OUTPUT: React-компонент баннера (или null, если выбор уже сделан или страница непубличная).
// KEYWORDS: DOMAIN(Compliance): CookieNotice; DOMAIN(Privacy): ConsentSeparation; TECH(React): ClientComponent
// END_MODULE_CONTRACT
//
// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.2.0 — 152-ФЗ в ред. 156-ФЗ (действует с 01.09.2025): из текста баннера удалена формулировка о подразумеваемом согласии на обработку ПДн, удалён POST /api/pdpn/consent, который фиксировал согласие по факту cookie-действия. Баннер теперь только информирует об использовании cookie и даёт два явных действия: «Принять» и «Только необходимые». Отказ пишется в отдельный ключ, чтобы не читаться как согласие на аналитику. Документ назван дословно по заголовку H1 страницы /privacy — «Политика обработки персональных данных».
// PREV_CHANGE_SUMMARY: 1.1.0 — BRAND v1.0: публичный вариант на токенах --portal-* (зелёный -> янтарь, radius 0, хард-тень); расширен список публичных префиксов (/tools, /login, /guard, /holdings и др.) — раньше там показывался CRM-вариант (синий)
// PREV_CHANGE_SUMMARY: 1.0.0 — Добавлена семантическая разметка (Ponytail audit: ступень 6)
// END_CHANGE_SUMMARY
//
// START_MODULE_MAP:
// FUNC [10] Компонент баннера cookie с двумя вариантами => CookieBanner
// FUNC [3] Проверка, является ли страница публичной => isPublicPage
// FUNC [5] Содержимое уведомления: заголовок, текст, кнопки выбора => NoticeContent
// END_MODULE_MAP

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Cookie, X } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import Link from "next/link";

const PUBLIC_PATH_PREFIXES = [
    "/rating", "/wiki", "/calc", "/uslugi", "/chop", "/claim", "/ref",
    "/tools", "/holdings", "/blog", "/login", "/register", "/pdpn",
    "/guard", "/guard-portal", "/partners", "/cabinet", "/contacts",
    "/offer", "/terms", "/fz-427", "/map", "/questionnaire", "/e",
];

function isPublicPage(pathname: string): boolean {
    if (pathname === "/" || pathname === "/consent" || pathname === "/privacy") return true;
    return PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// START_BLOCK_STORAGE_KEYS
// «Принять» — все cookie. Ключ сохранён с v1.0.0: его читает MetrikaConsent и после него
// инициализирует Яндекс.Метрику.
const ALL_COOKIES_KEY = "oxpa_cookie_consent";
// «Только необходимые» — отказ от аналитики. Отдельный ключ: см. MEMENTO ниже.
const ESSENTIAL_COOKIES_KEY = "oxpa_cookie_choice";
// END_BLOCK_STORAGE_KEYS

// START_BLOCK_NOTICE_STYLES
type NoticeStyles = {
    heading: string;
    text: string;
    link: string;
    primaryButton: string;
    secondaryButton: string;
    closeButton: string;
};

const CRM_STYLES: NoticeStyles = {
    heading: "font-semibold text-sm",
    text: "text-xs text-muted-foreground leading-relaxed",
    link: "underline hover:text-foreground font-medium",
    primaryButton: "font-medium",
    secondaryButton: "font-medium",
    closeButton: "text-muted-foreground hover:text-foreground transition-colors shrink-0",
};

const PORTAL_STYLES: NoticeStyles = {
    heading: "font-semibold text-sm text-[hsl(var(--portal-text))]",
    text: "text-xs text-[hsl(var(--portal-text-muted))] leading-relaxed",
    link: "underline text-[hsl(var(--portal-text-secondary))] hover:text-[hsl(var(--portal-accent))] font-medium transition-colors",
    primaryButton: "font-medium rounded-none uppercase tracking-widest text-[10px] bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))]",
    secondaryButton: "font-medium rounded-none uppercase tracking-widest text-[10px] border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text))] hover:bg-[hsl(var(--portal-surface-2))] hover:text-[hsl(var(--portal-text))]",
    closeButton: "text-[hsl(var(--portal-text-dim))] hover:text-[hsl(var(--portal-text))] transition-colors shrink-0",
};
// END_BLOCK_NOTICE_STYLES

// START_BLOCK_NOTICE_CONTENT: Текст и действия уведомления — общие для обоих вариантов,
// чтобы формулировки не разъезжались (CRM и Public показывают одно и то же по смыслу).
type NoticeContentProps = {
    styles: NoticeStyles;
    onAccept: () => void;
    onEssentialsOnly: () => void;
};

function NoticeContent({ styles, onAccept, onEssentialsOnly }: NoticeContentProps) {
    return (
        <>
            <h3 className={styles.heading}>
                Мы используем файлы cookie
            </h3>
            <p className={styles.text}>
                Файлы cookie нужны для работы сайта. Аналитика (Яндекс.Метрика) запускается
                только после нажатия «Принять»; отказ не ограничивает доступ к сайту.
                Об обработке данных — в документе «<Link href="/privacy" className={styles.link}>Политика обработки персональных данных</Link>».
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-3">
                <Button
                    onClick={onAccept}
                    size="sm"
                    className={`w-full sm:w-auto ${styles.primaryButton}`}
                >
                    Принять
                </Button>
                <Button
                    onClick={onEssentialsOnly}
                    size="sm"
                    variant="outline"
                    className={`w-full sm:w-auto ${styles.secondaryButton}`}
                >
                    Только необходимые
                </Button>
            </div>
        </>
    );
}
// END_BLOCK_NOTICE_CONTENT

export function CookieBanner() {
    const pathname = usePathname();
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        // Показываем уведомление, пока пользователь не сделал явный выбор ни одного из двух вариантов.
        const storedChoice =
            localStorage.getItem(ALL_COOKIES_KEY) || localStorage.getItem(ESSENTIAL_COOKIES_KEY);
        if (!storedChoice) {
            setShowBanner(true);
        }
    }, []);

    // «Принять» — все cookie, включая аналитические.
    const acceptCookies = () => {
        localStorage.setItem(ALL_COOKIES_KEY, "all");
        localStorage.removeItem(ESSENTIAL_COOKIES_KEY);
        setShowBanner(false);
        // Метрика не начинает сбор до согласия: после выбора уведомляем MetrikaConsent
        // кастомным событием для отложенной инициализации ym("init").
        window.dispatchEvent(new CustomEvent("oxpa:cookie-consent"));
        // <MEMENTO>
        // Здесь СОЗНАТЕЛЬНО нет POST /api/pdpn/consent: cookie-действие не является согласием
        // на обработку персональных данных (ч.1 ст.9 152-ФЗ в ред. 156-ФЗ — согласие конкретное,
        // предметное, информированное и сознательное, оформляется отдельным действием).
        // Не возвращать запись согласия в этот обработчик.
        // </MEMENTO>
    };

    // «Только необходимые» — аналитика не включается: событие oxpa:cookie-consent не отправляем.
    const essentialsOnly = () => {
        // <MEMENTO>
        // Отказ нельзя писать в oxpa_cookie_consent: metrika-consent.tsx считает согласием ЛЮБОЕ
        // непустое значение этого ключа и сразу вызывает ym("init"). Пока ключ один, «Только
        // необходимые» включало бы Яндекс.Метрику — то есть отказ работал бы как согласие.
        // Ключ oxpa_cookie_choice читает только этот компонент.
        // </MEMENTO>
        localStorage.setItem(ESSENTIAL_COOKIES_KEY, "essential");
        localStorage.removeItem(ALL_COOKIES_KEY);
        setShowBanner(false);
    };

    // Крестик — «скрыть уведомление»: это не выбор и не согласие, ничего не сохраняем,
    // поэтому при следующей загрузке страницы уведомление покажется снова.
    const dismissNotice = () => {
        setShowBanner(false);
    };

    if (!showBanner) return null;

    if (!isPublicPage(pathname)) {
        // CRM / Dashboard variant — corporate blue-white palette
        return (
            <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] bg-background border rounded-lg shadow-lg p-5 z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                        <Cookie className="w-6 h-6 text-primary mt-0.5 shrink-0" />
                        <div className="flex flex-col gap-2">
                            <NoticeContent
                                styles={CRM_STYLES}
                                onAccept={acceptCookies}
                                onEssentialsOnly={essentialsOnly}
                            />
                        </div>
                    </div>
                    <button
                        onClick={dismissNotice}
                        className={CRM_STYLES.closeButton}
                        aria-label="Скрыть уведомление"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    // Public / Portal variant — тёмная BRAND-тема на токенах --portal-* (янтарный акцент)
    return (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] rounded-none shadow-[4px_4px_0_hsl(var(--portal-bg))] p-5 z-[100] animate-in slide-in-from-bottom-5 fade-in duration-300 font-mono">
            <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                    <Cookie className="w-6 h-6 text-[hsl(var(--portal-accent))] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="flex flex-col gap-2">
                        <NoticeContent
                            styles={PORTAL_STYLES}
                            onAccept={acceptCookies}
                            onEssentialsOnly={essentialsOnly}
                        />
                    </div>
                </div>
                <button
                    onClick={dismissNotice}
                    className={PORTAL_STYLES.closeButton}
                    aria-label="Скрыть уведомление"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
